import "../../i18n";
import { useTranslation } from "react-i18next";
import { ConstructionPageData } from "../../Data/Translation/ConstructionPage/Data_ConstructionPage";
import "./ConstructionPage.scss"; // import CSS file
import { useInView } from "react-intersection-observer";
import { Variants, motion } from "framer-motion";
import image from '../../Assets/images/Pages/construction-image.jpg';
import SingleLayerIntroSection from "../../Components/Text/ParallaxTitle/Simple/SimpleParallaxTitle";
import '../../Styles/Shared/utilities.scss';
import useDetectView from "../../Components/Hook/useDetectView";
import { View } from "../../Data/Constants";
import { AngledBackgroundSection } from "../../Components/Text/AngledBackgroundTextSection/New version/AngledBackgroundSection";

interface InViewMotionDivProps {
    children: React.ReactNode;
    variants: Variants;
    className?: string;
}

const leftSideVariants: Variants = {
    hidden: { opacity: 0, x: -50 },
    visible: { opacity: 1, x: 0, transition: { duration: 1, ease: 'easeInOut' } }
};

const rightSideVariants: Variants = {
    hidden: { opacity: 0, x: 50 },
    visible: { opacity: 1, x: 0, transition: { duration: 1, ease: 'easeInOut' } }
};

const textZoomFadeVariants: Variants = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: { opacity: 1, scale: 1, transition: { duration: 0.8, ease: 'easeOut' } }
};

export default function Construction() {
    const { t, i18n } = useTranslation();
    const currentPageData = i18n.language === 'fr' ? ConstructionPageData.constructionPage_fr : ConstructionPageData.constructionPage_en;
    const currentView = useDetectView();

    return (
        <div>
            <div>
                <SingleLayerIntroSection
                    title={t("construction.title.title")}
                    backgroundImage={image}
                    subtitle=""
                    textBackground={{ enabled: true, color: "red", rounded: true, opacity: 0.5 }} />
            </div>
            <div className="construction-section-container">
                <div className="dual-content-section">
                    <InViewMotionDiv variants={leftSideVariants} className="left-side">
                        <motion.h3 variants={textZoomFadeVariants}>
                            {t("construction.phases.pre_dev.title")}
                        </motion.h3>
                        <motion.p className="justify-text" variants={textZoomFadeVariants}>
                            {t("construction.phases.pre_dev.text")}
                        </motion.p>
                    </InViewMotionDiv>
                    <InViewMotionDiv variants={rightSideVariants} className="right-side">
                        <ul className="services-list">
                            {currentPageData.phases.pre_dev.services.map(service => (
                                currentView !== View.Mobile ?
                                    <motion.li
                                        key={service}
                                        className="services-list-item"
                                        whileHover={{ color: "#101a2c", backgroundColor: "rgba(0, 0, 0, 0.05)", cursor: "pointer" }}
                                        initial={{ opacity: 0, y: 20 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ duration: 0.5 }}
                                    >
                                        {service}
                                    </motion.li>
                                    :
                                    <motion.li
                                        key={service}
                                        className="services-list-item"
                                        initial={{ opacity: 0, y: 20 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ duration: 0.1 }}
                                    >
                                        {service}
                                    </motion.li>
                            ))}
                        </ul>
                    </InViewMotionDiv>
                </div>
            </div>
            <AngledBackgroundSection
                backgroundColor="#0f1a2c"
                pageColor="#ffffff"
                contentPaddingY={0}
                contentPaddingX={0}
                top={{ type: "angle", height: 75, angleDirection: 'left' }}
                bottom={{ type: "angle", height: 75, angleDirection: 'left' }}
            >
                <div className="construction-section-container">
                    <div className="dual-content-section text-white">
                        <InViewMotionDiv variants={leftSideVariants} className="left-side">
                            <motion.h3 variants={textZoomFadeVariants}>
                                {t("construction.phases.conception.title")}
                            </motion.h3>
                            <motion.p className="justify-text" variants={textZoomFadeVariants}>
                                {t("construction.phases.conception.text")}
                            </motion.p>
                        </InViewMotionDiv>
                        <InViewMotionDiv variants={rightSideVariants} className="right-side">
                            <ul className="services-list">
                                {currentPageData.phases.conception.services.map(service => (
                                    currentView !== View.Mobile ?
                                        <motion.li
                                            key={service}
                                            className="services-list-item"
                                            whileHover={{ color: "white", backgroundColor: "rgba(255, 255, 255, 0.05)", cursor: "pointer" }}
                                            initial={{ opacity: 0, y: 20 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ duration: 0.1 }}
                                        >
                                            {service}
                                        </motion.li>
                                        :
                                        <motion.li
                                            key={service}
                                            className="services-list-item"
                                            initial={{ opacity: 0, y: 20 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ duration: 0.5 }}
                                        >
                                            {service}
                                        </motion.li>
                                ))}
                            </ul>
                        </InViewMotionDiv>
                    </div>
                </div>
            </AngledBackgroundSection>
            <div className="construction-section-container">
                <div className="dual-content-section">
                    <InViewMotionDiv variants={leftSideVariants} className="left-side">
                        <motion.h3 variants={textZoomFadeVariants}>
                            {t("construction.phases.management.title")}
                        </motion.h3>
                        <motion.p className="justify-text" variants={textZoomFadeVariants}>
                            {t("construction.phases.management.text")}
                        </motion.p>
                    </InViewMotionDiv>
                    <InViewMotionDiv variants={rightSideVariants} className="right-side">
                        <ul className="services-list">
                            {currentPageData.phases.management.services.map(service => (
                                currentView !== View.Mobile ?
                                    <motion.li
                                        key={service}
                                        className="services-list-item"
                                        whileHover={{ color: "#0f1a2c", backgroundColor: "rgba(0, 0, 0, 0.05)", cursor: "pointer" }}
                                        initial={{ opacity: 0, y: 20 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ duration: 0.5 }}
                                    >
                                        {service}
                                    </motion.li>
                                    :
                                    <motion.li
                                        key={service}
                                        className="services-list-item"
                                        initial={{ opacity: 0, y: 20 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ duration: 0.5 }}
                                    >
                                        {service}
                                    </motion.li>
                            ))}
                        </ul>
                    </InViewMotionDiv>
                </div>
            </div>
            <AngledBackgroundSection
                backgroundColor="#f3f3f3"
                pageColor="#ffffff"
                contentPaddingY={0}
                contentPaddingX={0}
                top={{ type: "angle", height: 75, angleDirection: 'right' }}
                bottom={{ type: "angle", height: 75, angleDirection: 'right' }}
            >
                <div className="construction-section-container">
                    <div className="dual-content-section">
                        <InViewMotionDiv variants={leftSideVariants} className="left-side">
                            <motion.h3 variants={textZoomFadeVariants}>
                                {t("construction.phases.construction.title")}
                            </motion.h3>
                            <motion.p className="justify-text" variants={textZoomFadeVariants}>
                                {t("construction.phases.construction.text")}
                            </motion.p>
                        </InViewMotionDiv>
                        <InViewMotionDiv variants={rightSideVariants} className="right-side">
                            <ul className="services-list">
                                {currentPageData.phases.construction.services.map(service => (
                                    currentView !== View.Mobile ?
                                        <motion.li
                                            key={service}
                                            className="services-list-item"
                                            whileHover={{ color: "#0f1a2c", backgroundColor: "rgba(0, 0, 0, 0.05)", cursor: "pointer" }}
                                            initial={{ opacity: 0, y: 20 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ duration: 0.5 }}
                                        >
                                            {service}
                                        </motion.li>
                                        :
                                        <motion.li
                                            key={service}
                                            className="services-list-item"
                                            initial={{ opacity: 0, y: 20 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ duration: 0.5 }}
                                        >
                                            {service}
                                        </motion.li>
                                ))}
                            </ul>
                        </InViewMotionDiv>
                    </div>
                </div>
            </AngledBackgroundSection>
            <div className="construction-section-container">
                <div className="dual-content-section">
                    <InViewMotionDiv variants={leftSideVariants} className="left-side">
                        <motion.h3 variants={textZoomFadeVariants}>
                            {t("construction.phases.post.title")}
                        </motion.h3>
                        <motion.p className="justify-text" variants={textZoomFadeVariants}>
                            {t("construction.phases.post.text")}
                        </motion.p>
                    </InViewMotionDiv>
                    <InViewMotionDiv variants={rightSideVariants} className="right-side">
                        <ul className="services-list">
                            {currentPageData.phases.post.services.map(service => (
                                currentView !== View.Mobile ?
                                    <motion.li
                                        key={service}
                                        className="services-list-item"
                                        whileHover={{ color: "#0f1a2c", backgroundColor: "rgba(0, 0, 0, 0.05)", cursor: "pointer" }}
                                        initial={{ opacity: 0, y: 20 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ duration: 0.5 }}
                                    >
                                        {service}
                                    </motion.li>
                                    :
                                    <motion.li
                                        key={service}
                                        className="services-list-item"
                                        initial={{ opacity: 0, y: 20 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ duration: 0.5 }}
                                    >
                                        {service}
                                    </motion.li>
                            ))}
                        </ul>
                    </InViewMotionDiv>
                </div>
            </div>
        </div >
    );
}

const InViewMotionDiv: React.FC<InViewMotionDivProps> = ({ children, variants, className }) => {
    const [ref, inView] = useInView({ triggerOnce: true });

    return (
        <motion.div
            ref={ref}
            variants={variants}
            initial="hidden"
            animate={inView ? "visible" : "hidden"}
            className={className}
        >
            {children}
        </motion.div>
    );
};
